import { CSSProperties, ReactElement } from 'react'
import { Tooltip } from 'app/components/Tooltip'
import { hueClasses } from 'app/lib/chipColors'
import { cn } from 'app/lib/utils'
import type { FunnelStage } from './smsMetricDefinitions'

/**
 * AI conversations → patient replied → booked, as horizontal bars sized against the
 * first stage (plan D15). Each later stage also shows its share of the stage before.
 */
export function AiFunnel({ stages, isLoading }: { stages: FunnelStage[]; isLoading: boolean }): ReactElement {
  const first = stages[0]?.count ?? 0
  return (
    <div className="space-y-3 rounded-lg border border-default p-4">
      {stages.map((stage, index) => {
        const previous = stages[index - 1]?.count
        // A stage with any count keeps a sliver of bar, so it never reads as zero.
        const share = isLoading || !first ? 0 : Math.max((stage.count / first) * 100, stage.count ? 1 : 0)
        return (
          <div key={stage.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 md:flex-nowrap">
            <Tooltip message={stage.definition} placement="bottom">
              <span className="block w-36 shrink-0 text-ui-sm text-default md:w-44">{stage.label}</span>
            </Tooltip>
            <div className="h-7 min-w-0 flex-1 rounded bg-surface-muted">
              <div
                className={cn(`h-full w-(--funnel-share) rounded border`, hueClasses.purple)}
                style={{ '--funnel-share': `${share}%` } as CSSProperties}
              />
            </div>
            <div className="w-full text-body-sm text-subtle md:w-36 md:text-right">
              <span className="text-ui-sm text-strong">{isLoading ? `-` : stage.count.toLocaleString(`en-US`)}</span>
              {index > 0 && !isLoading && previous
                ? ` · ${((stage.count / previous) * 100).toFixed(0)}% of previous`
                : ``}
            </div>
          </div>
        )
      })}
    </div>
  )
}
